import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, LoadingController } from '@ionic/angular';

import { DateRange } from 'src/app/components/date-range-picker/date-range-picker.component';
import { DateRangePickerService } from 'src/app/components/date-range-picker/date-range-picker.service';
import { Tenant } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { MessageService } from 'src/services/message.service';

import { SharedTenantService } from '../../super-admin-taxonomy/shared-tenant.service';

interface KnowledgeGapTopic {
  id: string;
  conversationId: string;
  topicTitle: string;
  topicDescription: string;
  context: string;
  confidence: string;
  createdAt: string;
}

interface InsightAction {
  type: 'create_new' | 'update_existing';
  description: string;
  topics: string[];
  signalType: 'knowledge_gap';
  isDismissed: boolean;
}

interface KnowledgeGapInsight {
  id: string;
  tenantId: string;
  projectId: string;
  category: string;
  language: string;
  topics: KnowledgeGapTopic[];
  actions: InsightAction[];
  createdAt: string;
  updatedAt: string;
}

interface KnowledgeGapStats {
  total: number;
  byCategory: { [category: string]: number };
}

interface KnowledgeGapInsightsResponse {
  insights: KnowledgeGapInsight[];
  meta: {
    total: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

interface TriggerAnalysisResponse {
  message: string;
  conversationsQueued: number;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  projectId?: string;
}

interface ResetDataResponse {
  message: string;
  deletedCounts: {
    insights: number;
    topics: number;
    recommendations: number;
  };
  scope: string;
}

@Component({
  selector: 'app-knowledge-gaps-tenant-insights',
  templateUrl: './knowledge-gaps-tenant-insights.component.html',
  styleUrls: ['./knowledge-gaps-tenant-insights.component.scss'],
})
export class KnowledgeGapsTenantInsightsComponent implements OnInit {
  insights: KnowledgeGapInsight[] = [];
  stats: KnowledgeGapStats | null = null;
  selectedInsight: KnowledgeGapInsight | null = null;
  selectedTenant: Partial<Tenant> | null = null;
  tenantId: string | null = null;

  isLoading = false;
  isLoadingStats = false;
  isLoadingInsights = false;
  error: string | null = null;

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalInsights = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  // Filters
  selectedProjectId: string | null = null;
  searchText?: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private messageService: MessageService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private dateRangePickerService: DateRangePickerService,
    private sharedTenantService: SharedTenantService,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.tenantId = params.get('tenantId');
      if (this.tenantId) {
        this.selectedTenant = this.sharedTenantService.getSelectedTenant();
        this.loadInsights();
        this.loadStats();
      }
    });
  }

  async loadInsights() {
    if (!this.tenantId) return;

    this.isLoadingInsights = true;
    this.error = null;

    try {
      const params: any = {
        page: this.currentPage,
        pageSize: this.pageSize,
      };

      if (this.selectedProjectId) params.projectId = this.selectedProjectId;
      if (this.searchText) params.search = this.searchText;

      const response = (await this.apiService.postPromise(
        `/tenants/${this.tenantId}/knowledge-gaps/insights`,
        params,
      )) as KnowledgeGapInsightsResponse;

      this.insights = response.insights;
      this.totalInsights = response.meta.total;
      this.hasNextPage = response.meta.hasNext;
      this.hasPreviousPage = response.meta.hasPrevious;
    } catch (error) {
      this.error = 'Failed to load knowledge gap insights: ' + (error.message || 'Unknown error');
    } finally {
      this.isLoadingInsights = false;
    }
  }

  async loadStats() {
    if (!this.tenantId) return;

    this.isLoadingStats = true;

    try {
      const params: any = {};
      if (this.selectedProjectId) params.projectId = this.selectedProjectId;

      const response = (await this.apiService.getPromise(
        `/tenants/${this.tenantId}/knowledge-gaps/stats`,
        params,
      )) as KnowledgeGapStats;

      this.stats = response;
    } catch (error) {
      this.error = 'Failed to load knowledge gap statistics: ' + (error.message || 'Unknown error');
    } finally {
      this.isLoadingStats = false;
    }
  }

  onRefresh() {
    this.loadInsights();
    this.loadStats();
  }

  goBackToTenantSelection() {
    this.router.navigate(['/super-admin/knowledge-gaps']);
  }

  // Pagination methods
  async nextPage() {
    if (this.hasNextPage) {
      this.currentPage++;
      await this.loadInsights();
    }
  }

  async previousPage() {
    if (this.hasPreviousPage && this.currentPage > 1) {
      this.currentPage--;
      await this.loadInsights();
    }
  }

  // Filter methods
  async applyFilters() {
    this.currentPage = 1; // Reset to first page when applying filters
    await this.loadInsights();
  }

  clearFilters() {
    this.selectedProjectId = null;
    this.searchText = undefined;
    this.applyFilters();
  }

  onSearch() {
    this.loadInsights();
  }

  // Utility methods
  getConfidenceColor(score: number): string {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    return 'danger';
  }

  getConfidenceLabel(score: number): string {
    if (score >= 0.8) return 'High';
    if (score >= 0.6) return 'Medium';
    return 'Low';
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }

  formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString();
  }

  selectInsight(insight: KnowledgeGapInsight) {
    this.selectedInsight = insight;
  }

  clearInsightSelection() {
    this.selectedInsight = null;
  }

  viewInsightDetail(insightId: string) {
    this.router.navigate(['/super-admin/knowledge-gaps', this.tenantId, 'insights', insightId]);
  }

  getTotalConversationCount(insight: any): number {
    if (!insight || !insight.topics) return 0;
    // Count unique conversations from topics
    const uniqueConversationIds = new Set(insight.topics.map((topic: any) => topic.conversationId));
    return uniqueConversationIds.size;
  }

  getUniqueConversationCount(topics: any[]): number {
    if (!topics || topics.length === 0) return 0;
    const uniqueConversationIds = new Set(topics.map((topic) => topic.conversationId));
    return uniqueConversationIds.size;
  }

  // Stats helper methods
  getCategoryNames(): string[] {
    return this.stats?.byCategory ? Object.keys(this.stats.byCategory) : [];
  }

  getTotalPages(): number {
    return Math.ceil(this.totalInsights / this.pageSize);
  }

  getEndRange(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalInsights);
  }

  async triggerAnalysis() {
    if (!this.tenantId) return;

    // Step 1: Get date range using the nice date picker
    const defaultDateRange: DateRange = {
      startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
      endDate: new Date(),
      type: 'relative',
      label: 'Last 30 days',
    };

    const selectedDateRange = await this.dateRangePickerService.openDateRangePicker(defaultDateRange);

    if (!selectedDateRange) {
      return; // User cancelled date selection
    }

    // Step 2: Get optional project ID via simple alert
    const projectAlert = await this.alertController.create({
      header: 'Project Scope (Optional)',
      message: 'Leave blank to analyze all projects for this tenant.',
      inputs: [
        {
          name: 'projectId',
          type: 'text',
          placeholder: 'Project ID (optional)',
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Start Analysis',
          handler: async (data) => {
            await this.performTriggerAnalysis(selectedDateRange, data.projectId);
          },
        },
      ],
    });

    await projectAlert.present();
  }

  private async performTriggerAnalysis(dateRange: DateRange, projectId?: string) {
    if (!this.tenantId || !dateRange) return;

    // Convert dates to ISO 8601 format
    const startDateTime = dateRange.startDate.toISOString();
    const endDateTime = dateRange.endDate.toISOString();

    // Validate date range (already validated by date picker, but double check)
    if (dateRange.startDate >= dateRange.endDate) {
      this.messageService.showError('Start date must be before end date');
      return;
    }

    const loading = await this.loadingController.create({
      message: 'Starting knowledge gap analysis...',
    });

    try {
      await loading.present();
      this.isLoading = true;

      const payload: any = {
        startDate: startDateTime,
        endDate: endDateTime,
      };

      if (projectId && projectId.trim()) {
        payload.projectId = projectId.trim();
      }

      const response = (await this.apiService.postPromise(
        `/tenants/${this.tenantId}/knowledge-gaps/trigger-analysis`,
        payload,
      )) as TriggerAnalysisResponse;

      this.messageService.show(
        `Knowledge gap analysis started successfully. ${response.conversationsQueued} conversations queued for analysis.`,
      );

      // Refresh data after a short delay to show any immediate updates
      setTimeout(() => {
        this.onRefresh();
      }, 2000);
    } catch (error) {
      this.messageService.showError('Failed to trigger knowledge gap analysis: ' + (error.message || 'Unknown error'));
    } finally {
      this.isLoading = false;
      await loading.dismiss();
    }
  }

  async resetData() {
    if (!this.tenantId) return;

    const alert = await this.alertController.create({
      header: 'Reset Knowledge Gap Data',
      message: `⚠️ This will permanently delete knowledge gap data for ${this.selectedTenant?.name || 'this tenant'}. This action cannot be undone.`,
      inputs: [
        {
          name: 'confirmTenantId',
          type: 'text',
          placeholder: 'Enter tenant ID to confirm',
          value: '',
        },
        {
          name: 'projectId',
          type: 'text',
          placeholder: 'Project ID (optional - leave blank to reset entire tenant)',
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Delete Data',
          role: 'destructive',
          handler: async (data) => {
            if (data.confirmTenantId !== this.tenantId) {
              this.messageService.showError('Tenant ID confirmation does not match. Operation cancelled.');
              return false;
            }
            await this.performResetData(data.projectId);
            return true;
          },
        },
      ],
    });

    await alert.present();
  }

  private async performResetData(projectId?: string) {
    if (!this.tenantId) return;

    const loading = await this.loadingController.create({
      message: 'Resetting knowledge gap data...',
    });

    try {
      await loading.present();
      this.isLoading = true;

      const payload: any = {
        confirmTenantId: this.tenantId,
      };

      if (projectId && projectId.trim()) {
        payload.projectId = projectId.trim();
      }

      const response = (await this.apiService.deletePromise(
        `/tenants/${this.tenantId}/knowledge-gaps/reset-data`,
        undefined,
        payload,
      )) as ResetDataResponse;

      const scopeText = projectId ? `project ${projectId}` : 'entire tenant';
      const deletedText = `${response.deletedCounts.insights} insights, ${response.deletedCounts.topics} topics, ${response.deletedCounts.recommendations} recommendations`;

      this.messageService.show(`Knowledge gap data reset successfully for ${scopeText}. Deleted: ${deletedText}`);

      // Clear local data immediately if resetting entire tenant
      if (!projectId) {
        this.insights = [];
        this.stats = null;
        this.totalInsights = 0;
        this.currentPage = 1;
      }

      // Refresh to confirm updated state
      this.onRefresh();
    } catch (error) {
      this.messageService.showError('Failed to reset knowledge gap data: ' + (error.message || 'Unknown error'));
    } finally {
      this.isLoading = false;
      await loading.dismiss();
    }
  }

  parseFloat(value: string): number {
    return parseFloat(value);
  }

  trackByInsight(index: number, insight: KnowledgeGapInsight): string {
    return insight.id;
  }

  getSynopsis(insight: KnowledgeGapInsight): string {
    if (!insight.topics || insight.topics.length === 0) {
      return '';
    }

    const totalTopics = insight.topics.length;
    const totalConversations = this.getTotalConversationCount(insight);

    return `Analysis of ${totalConversations} conversations identified ${totalTopics} recurring topics in ${insight.category}.`;
  }

  getTopicsFromInsight(insight: KnowledgeGapInsight) {
    return insight.topics || [];
  }

  getConversationUrlForTopic(topic: { conversationId: string }): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/dashboard/conversations/${topic.conversationId}`;
  }

  getTopicDeepLink(topic: KnowledgeGapTopic): string {
    const baseUrl = window.location.origin;
    // Link to the knowledge gaps topic detail component
    return `${baseUrl}/super-admin/knowledge-gaps/${this.tenantId}/topics/${topic.id}`;
  }

  async viewConversationsForInsight(insight: KnowledgeGapInsight, event: Event) {
    event.preventDefault();
    this.viewInsightDetails(insight);
  }

  getActionForTopic(insight: KnowledgeGapInsight, topic: KnowledgeGapTopic): InsightAction | null {
    if (!insight.actions || insight.actions.length === 0) return null;

    // Find action that references this topic
    return insight.actions.find((action) => !action.isDismissed && action.topics.includes(topic.id)) || null;
  }

  getActionButtonText(action: InsightAction): string {
    switch (action.type) {
      case 'create_new':
        return 'Generate Article';
      case 'update_existing':
        return 'Update Article';
      default:
        return 'Execute Action';
    }
  }

  async executeTopicAction(topic: KnowledgeGapTopic, action: InsightAction) {
    // For super admin, navigate to the article editor in a new tab with topicId
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/dashboard/knowledge/articles/new?topicId=${topic.id}&from=insights`;
    window.open(url, '_blank');
  }

  async dismissTopicAction(insight: KnowledgeGapInsight, topic: KnowledgeGapTopic, action: InsightAction) {
    const loading = await this.loadingController.create({
      message: 'Dismissing action...',
    });
    await loading.present();

    try {
      // Use tenant-prefixed insight-level endpoint for admin
      await this.apiService.postPromise(
        `/tenants/${this.tenantId}/knowledge-gaps/insights/${insight.id}/actions/dismiss`,
        {
          actionType: action.type,
          topicId: topic.id,
        },
      );
      await loading.dismiss();
      this.messageService.show('Action dismissed successfully.');
      // Refresh insights to get updated action status
      await this.loadInsights();
    } catch (error: any) {
      await loading.dismiss();
      this.messageService.showError('Failed to dismiss action: ' + (error.message || 'Unknown error'));
    }
  }

  viewInsightDetails(insight: KnowledgeGapInsight) {
    this.selectedInsight = insight;
  }

  toggleInsightExpansion(insight: KnowledgeGapInsight) {
    if (this.selectedInsight?.id === insight.id) {
      this.selectedInsight = null;
    } else {
      this.selectedInsight = insight;
    }
  }
}
